import styled from '@emotion/styled';
import {
  Grounder,
  InnerHtml,
  Skeleton,
  Typography,
} from '@innovamat/glimmer-components';
import { usePage } from '@innovamat/glow-api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetAppletsOfTheWeek } from '../../../classrooms-management/hooks/use-get-applets-of-the-week';
import DigitalPracticeInformation from '../../../digital-practice-information/digital-practice-information';
import { ERROR_TYPE, ErrorPage } from '../../../navigation';
import { useNotifications } from '../../../notifications/notifications-provider';
import type { MarkSessionMethodParams } from '../../../session-done';
import { useShowDigitalPracticeInformation } from '../../../user-management/hooks/use-show-digital-practice-information';
import {
  ContentList,
  ContentListSkeleton,
} from '../../components/content-list';
import { SkeletonContainer } from '../../components/content-list/content-list.styled';
import { useCurrentNavigationValues } from '../../hooks';
import type { ContentListType, ContentResource, Page } from '../../types/page';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

type ContentListsPageProps = {
  academicYearId: string;
  banners?: JSX.Element;
  classroomId?: string;
  imgOptimizationUrl?: string;
  isPaginationEnabled: boolean;
  isStudentView?: boolean;
  leadAnnouncementPanel?: JSX.Element;
  pageId?: string;
  title?: string;
  yearUpdateAnnouncementPanel?: JSX.Element;
  region: string;
  language: string;
  onError?: () => void;
  onMarkSession?: (params: MarkSessionMethodParams) => void;
  onResourceClick: (
    resource: ContentResource,
    list: ContentListType,
    page: Page
  ) => void;
  onShowMoreClick?: (newPageId: string, listId: string) => void;
  onToggleSolutions?: (params: MarkSessionMethodParams) => void;
  setEventData: (
    eventType: string,
    eventProperties?: any,
    addUserData?: boolean
  ) => void;
};

function ContentListPageSkeleton(): JSX.Element {
  return (
    <SkeletonContainer>
      <Skeleton height="32" width="140" />
      <ContentListSkeleton />
      <ContentListSkeleton />
      <ContentListSkeleton />
    </SkeletonContainer>
  );
}

export function ContentPage({
  academicYearId,
  banners,
  classroomId,
  imgOptimizationUrl,
  isPaginationEnabled,
  isStudentView,
  leadAnnouncementPanel,
  pageId,
  title,
  yearUpdateAnnouncementPanel,
  region,
  language,
  onError,
  onMarkSession,
  onResourceClick,
  onShowMoreClick,
  onToggleSolutions,
}: ContentListsPageProps): JSX.Element {
  const hasPageId = !!pageId;

  const { courseOrder } = useCurrentNavigationValues();
  const queryClient = useQueryClient();
  const { setSessionDoneNotification, sessionDoneNotification } =
    useNotifications();
  const digitalPracticeRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const { data, isLoading, isError } = usePage({
    pageId: pageId!,
    academicYearId: academicYearId!,
    classroomId: classroomId!,
    isStudentView: Boolean(isStudentView),
    paginated: isPaginationEnabled,
    region,
    language,
  });

  const { showDigitalPracticeInformation } = useShowDigitalPracticeInformation({
    courseOrder,
    classroomId,
  });

  const {
    appletsOfTheWeek,
    isLoading: isAppletsOfTheWeekLoading,
    isFetching: isAppletsOfTheWeekFetching,
    refetch: refetchAppletsOfTheWeek,
  } = useGetAppletsOfTheWeek({
    classroomId: classroomId!,
    locale: language,
    enabled: showDigitalPracticeInformation,
  });

  const page = data as unknown as Page;

  const isFirstRender = useRef(true);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const isInitialLoading =
    isFirstRender.current &&
    (isAppletsOfTheWeekLoading || isAppletsOfTheWeekFetching);

  useEffect(() => {
    if (!showDigitalPracticeInformation) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSessionDoneNotification(false);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (digitalPracticeRef.current) {
      observer.observe(digitalPracticeRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [showDigitalPracticeInformation, setSessionDoneNotification]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (
      (searchParams.has('toDigitalPracticeSlider') ||
        sessionDoneNotification) &&
      digitalPracticeRef.current &&
      !isLoading &&
      !isInitialLoading &&
      hasPageId &&
      page
    ) {
      setTimeout(() => {
        digitalPracticeRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }, 2000);
    }
  }, [
    location,
    digitalPracticeRef,
    isLoading,
    isInitialLoading,
    hasPageId,
    page,
    sessionDoneNotification,
  ]);

  if (!isError && (isLoading || isInitialLoading || !hasPageId || !page)) {
    return <ContentListPageSkeleton />;
  }

  if (isError || !page) {
    return <ErrorPage errorType={ERROR_TYPE.NOT_AVAILABLE} />;
  }

  const handleOnMarkSession = (params: MarkSessionMethodParams): void => {
    const onMarkSessionFinished = (): void => {
      if (params.value === false) {
        queryClient.invalidateQueries({ queryKey: ['getAppletsOfTheWeek'] });
      }
    };

    const paramsWithOnMarkSessionFinished = {
      ...params,
      onMarkSessionFinished,
    };

    if (onMarkSession) {
      onMarkSession(paramsWithOnMarkSessionFinished);
    }
  };

  return (
    <>
      <Container id="content-page">
        {leadAnnouncementPanel}
        <Typography.H3 data-cy="contentTitle">
          <InnerHtml text={title || page.name} />
        </Typography.H3>
        {banners}
        {yearUpdateAnnouncementPanel}

        {page?.content_lists?.map((list, index) => {
          return (
            <ContentList
              isPaginationEnabled={isPaginationEnabled}
              key={index}
              list={list}
              onResourceClick={(resource) => {
                onResourceClick(resource, list, page);
              }}
              onShowMoreClick={
                list.page_id && onShowMoreClick
                  ? () => onShowMoreClick(list.page_id, list.id)
                  : undefined
              }
              imgOptimizationUrl={imgOptimizationUrl}
              onMarkSession={handleOnMarkSession}
              onToggleSolutions={onToggleSolutions}
              academicYearId={academicYearId}
              classroomId={classroomId}
              isStudentView={isStudentView}
              rowIndex={index}
            />
          );
        })}
        {showDigitalPracticeInformation && (
          <div ref={digitalPracticeRef}>
            <DigitalPracticeInformation
              appletsOfTheWeek={appletsOfTheWeek}
              courseOrder={courseOrder || 0}
              onConfigurationChange={() => {
                setTimeout(() => {
                  refetchAppletsOfTheWeek();
                }, 2000);
              }}
              isVisible={showDigitalPracticeInformation}
            />
          </div>
        )}
      </Container>
      <Grounder />
    </>
  );
}
